import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'pwa-install-dismissed';

// Ранее использовалось для позиции полосы; сейчас позиция кнопки корзины задаётся через --pwa-bar-height
const CART_ZONE_HEIGHT = 72;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches;

/**
 * Установка PWA:
 * - Не показываем свою полосу, по клику открывается нативный диалог браузера (как при «Перевести»).
 * - На Android/ПК: сохраняем событие beforeinstallprompt и показываем одну небольшую кнопку «Установить» — по нажатию вызывается только нативный UI браузера.
 * - На iOS: показываем короткую инструкцию (Поделиться → На экран Домой), т.к. beforeinstallprompt там нет.
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showManualHint, setShowManualHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === 'true') {
      setDismissed(true);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const timer = isIOS()
      ? window.setTimeout(() => setShowManualHint(true), 1500)
      : null;

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (deferredPrompt) setShowManualHint(false);
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowManualHint(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const barVisible = !dismissed && (!!deferredPrompt || showManualHint);

  useEffect(() => {
    if (barVisible) {
      // На телефоне полоса выше из‑за переноса текста — поднимаем кнопку корзины сильнее
      const isMobile = window.innerWidth < 768;
      document.body.style.setProperty('--pwa-bar-height', isMobile ? '100px' : '56px');
    } else {
      document.body.style.removeProperty('--pwa-bar-height');
    }
    return () => document.body.style.removeProperty('--pwa-bar-height');
  }, [barVisible]);

  if (dismissed) return null;

  // Полоса внизу экрана; кнопка корзины поднимается за счёт --pwa-bar-height в CustomerMenu
  if (deferredPrompt) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '10px 16px',
          background: 'rgba(0,0,0,0.85)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          zIndex: 9998,
          fontSize: '14px',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
        }}
      >
        <span>Установить приложение для быстрого доступа к меню</span>
        <button
          type="button"
          onClick={handleInstall}
          style={{
            padding: '6px 14px',
            background: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Установить
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            padding: '6px 10px',
            background: 'transparent',
            color: 'rgba(255,255,255,0.8)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Позже
        </button>
      </div>
    );
  }

  // iOS: полоса внизу с инструкцией
  if (showManualHint) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 16px',
          background: 'rgba(0,0,0,0.85)',
          color: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          zIndex: 9998,
          fontSize: '14px',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
        }}
      >
        <span>
          Добавить на главный экран: в Safari нажмите <strong>Поделиться</strong> (□↑) → <strong>«На экран „Домой“»</strong>
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            padding: '6px 10px',
            background: 'transparent',
            color: 'rgba(255,255,255,0.8)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Позже
        </button>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
